import React from 'react';
import { useJaws } from '@oms/jaws-react';
import { NoData, Loading } from '@oms/components-core';
import { Trans, t } from '@lingui/macro';
import { Text } from '@oms/ui-text';
import { Heading } from '@oms/ui-heading';
import { VisuallyHidden } from '@oms/ui-visually-hidden';
import { Box } from '@oms/ui-box';
import { Icon, light } from '@oms/ui-icon';
import { ModuleSection, ModuleProps } from 'components/Module';
import { MarketBadge } from 'components/MarketBadge';
import { getTrendColor } from 'utils/trendColor';
import { formatTime, formatNumberWithAbbr } from './util';
import { LabelDT, ValueDD } from './styles';
import { formatNumber } from '@oms/utils';
import { useLanguage } from '@oms/ui-language-context';

type GridItemProps = {
  children: React.ReactNode;
};

const GridItem = ({ children }: GridItemProps) => (
  <Box
    display="flex"
    flexDirection="column"
    width={{ base: 1 / 2, sm: 1 / 3, lg: 1 / 6 }}
    height={{ base: '3rem', sm: '2.5rem' }}
  >
    {children}
  </Box>
);

export default function Snapshot({ token, ...styleProps }: ModuleProps) {
  const { emptyData, initialized, resource, items } = useJaws({
    product: 'snapshot',
  });

  const lang = useLanguage();

  if (!initialized) {
    if (!resource) return null;
    return <Loading promise={resource.promise} />;
  }

  if (emptyData) {
    return <NoData text={t`No data found`} />;
  }
  const {
    ITEM,
    SECTOR,
    LONG_NAME,
    TIME,
    LAST,
    CHANGE,
    CHANGE_PCT,
    ASK,
    BID,
    VOLUME,
    TURNOVER,
    CURRENCY,
    MARKET_CAP,
    HIGH,
    LOW,
  } = items.first().toJS();

  return (
    <ModuleSection {...styleProps}>
      <Heading as="h1" variant="heading3" mb={-12} data-testid="longName">
        {LONG_NAME}
      </Heading>
      <dl>
        <Box display="flex" flexDirection="row" mb={3} alignItems="flex-end">
          <Box flex={1}>
            <Box>
              <VisuallyHidden as="dt">Ticker</VisuallyHidden>
              <Text as="dd" data-testid="item">
                {ITEM}
              </Text>
            </Box>
          </Box>

          <Box>
            <Box data-testid="badge">
              <VisuallyHidden as="dt">
                <Trans>Exchange</Trans>
              </VisuallyHidden>
              <dd>
                <MarketBadge ticker={ITEM} sector={SECTOR as string} />
              </dd>
            </Box>
          </Box>
        </Box>

        {/* start second row/column */}
        <Box
          display="flex"
          flexDirection={{ base: 'column', md: 'row' }}
          p={3}
          borderWidth="sm"
        >
          {/* start first column */}
          <Box
            display="flex"
            flexDirection="column"
            justifyContent={{ base: 'space-between', md: 'initial' }}
          >
            <Box display="flex" data-testid="lastBox">
              <VisuallyHidden as="dt">
                <Trans>Last</Trans>
              </VisuallyHidden>
              <Box as="dd" display="flex" flexDirection="row" height="2.5rem">
                <Text
                  fontSize={{ base: 'md', md: 'lg' }}
                  mt={1}
                  aria-label="currency"
                  data-testid="currency"
                >
                  {CURRENCY}
                </Text>
                <Text
                  fontSize={{ base: '3xl', md: '3xl' }}
                  ml={2}
                  data-testid="last"
                >
                  {formatNumber(LAST)}
                </Text>
              </Box>
            </Box>
            {/* - */}
            <Box mt={{ base: 2, md: 0 }} data-testid="updated">
              <VisuallyHidden as="dt">
                <Trans>Updated</Trans>
              </VisuallyHidden>
              <Text
                as="dd"
                // color="text-light"
                fontSize="sm"
              >
                <Box as="span" display="inline" mr={1}>
                  <Icon icon={light.faClock} />
                </Box>
                <time data-testid="time">
                  {TIME != null ? formatTime(TIME) : ''}
                </time>
              </Text>
            </Box>
          </Box>
          {/* end first column */}
          {/* start second column */}
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            pl={{ base: 0, md: 6 }}
            mt={{ base: 4, md: 0 }}
            data-testid="dataTable"
          >
            <Box display="flex" flexWrap="wrap">
              <GridItem>
                <LabelDT>
                  <Trans>Change</Trans> %
                </LabelDT>
                <Box
                  as="dd"
                  display="flex"
                  flexDirection="row"
                  alignItems="flex-end"
                  m={0}
                >
                  <Text color={getTrendColor(CHANGE)} data-testid="change">
                    {formatNumber(CHANGE)}
                  </Text>
                  <Text
                    color={getTrendColor(CHANGE)}
                    ml={2}
                    fontSize="sm"
                    mr={1}
                    data-testid="changePct"
                  >
                    ({formatNumber(CHANGE_PCT)}%)
                  </Text>
                </Box>
              </GridItem>
              {/* - */}
              <GridItem>
                <LabelDT>
                  <Trans>Buy</Trans> / <Trans>Sell</Trans>
                </LabelDT>
                <ValueDD data-testid="buySell">
                  {/* TODO separate values in <Text/> components */}
                  {formatNumber(BID)} / {formatNumber(ASK)}
                </ValueDD>
              </GridItem>
              {/* - */}
              <GridItem>
                <LabelDT>
                  <Trans>High</Trans> / <Trans>Low</Trans>
                </LabelDT>
                <ValueDD data-testid="highLow">
                  {/* TODO separate values in <Text/> components */}
                  {formatNumber(HIGH)} / {formatNumber(LOW)}
                </ValueDD>
              </GridItem>
              {/* - */}
              <GridItem>
                <LabelDT>
                  <Trans>Turnover</Trans>
                </LabelDT>
                <ValueDD data-testid="turnover">
                  {formatNumberWithAbbr(TURNOVER, lang)}
                </ValueDD>
              </GridItem>
              {/* - */}
              <GridItem>
                <LabelDT>
                  <Trans>Market cap.</Trans>
                </LabelDT>
                <ValueDD data-testid="marketCap">
                  {formatNumberWithAbbr(MARKET_CAP, lang)}
                </ValueDD>
              </GridItem>
              {/* - */}
              <GridItem>
                <LabelDT>
                  <Trans>Volume</Trans>
                </LabelDT>
                <ValueDD data-testid="volume">
                  {formatNumber(VOLUME, 0)}
                </ValueDD>
              </GridItem>
            </Box>
          </Box>
          {/* end second column */}
        </Box>
      </dl>
    </ModuleSection>
  );
}

export { Snapshot };
